/* eslint-disable */
import { lazy } from 'react';
import { USER_ROLE } from 'constants.js';
import { DEFAULT_PATHS } from 'config.js';

// Import account components directly (not lazy) to avoid webpack issues
import AccountSettings from 'views/account/AccountSettings';
import AccountBilling from 'views/account/AccountBilling';
import AccountSecurity from 'views/account/AccountSecurity';

const admin = {
  login: lazy(() => import('views/admin/Login')),
  dashboard: lazy(() => import('views/admin/Dashboard')),
  users: lazy(() => import('views/admin/Users')),
  userDetails: lazy(() => import('views/admin/UserDetails')),
  register: lazy(() => import('views/admin/Register')),
  pendingUsers: lazy(() => import('views/admin/PendingUsers')),
  userManagement: lazy(() => import('views/admin/UserManagement')),
  admins: lazy(() => import('views/admin/Admins')),
  families: lazy(() => import('views/admin/Families')),
  providers: lazy(() => import('views/admin/Providers')),
  corporateOrganizations: lazy(() => import('views/admin/CorporateOrganizations')),
  corporateOrganizationDetails: lazy(() => import('views/admin/CorporateOrganizationDetails')),
  eventManagement: lazy(() => import('views/admin/events/EventManagement')),
  eventTickets: lazy(() => import('views/admin/events/EventTickets')),
  eventBookings: lazy(() => import('views/admin/events/EventBookings')),
  pendingApprovals: lazy(() => import('views/admin/events/PendingApprovals')),
  programTemplates: lazy(() => import('views/admin/ProgramTemplates')),
};

const defaultPages = {
  login: lazy(() => import('views/default/Login')),
  register: lazy(() => import('views/admin/Register')),
};

const blog = lazy(() => import('views/blog/Blog'));
const community = {
  index: lazy(() => import('views/community/Community')),
  list: lazy(() => import('views/community/CommunityList')),
};
const upgrade = lazy(() => import('views/upgrade/Upgrade'));

const dashboard = {
  gettingStarted: lazy(() => import('views/dashboard/DashboardGettingStarted')),
  analysis: lazy(() => import('views/dashboard/DashboardAnalysis')),
};
const services = {
  database: lazy(() => import('views/services/ServicesDatabase')),
  databaseAdd: lazy(() => import('views/services/ServicesDatabaseAdd')),
  databaseDetail: lazy(() => import('views/services/ServicesDatabaseDetail')),
  storage: lazy(() => import('views/services/ServicesStorage')),
  storageFolder: lazy(() => import('views/services/ServicesStorageFolder')),
  hosting: lazy(() => import('views/services/ServicesHosting')),
  users: lazy(() => import('views/services/ServicesUsers')),
};
const account = {
  settings: AccountSettings,
  billing: AccountBilling,
  security: AccountSecurity,
};
const support = {
  docs: lazy(() => import('views/support/SupportDocs')),
  docsDetail: lazy(() => import('views/support/SupportDocsDetail')),
  knowledgeBase: lazy(() => import('views/support/SupportKnowledgeBase')),
  tickets: lazy(() => import('views/support/SupportTickets')),
  ticketsDetail: lazy(() => import('views/support/SupportTicketsDetail')),
};

const wellness = {
  dashboard: lazy(() => import('views/wellness/WellnessDashboard')),
  organizations: lazy(() => import('views/wellness/OrganizationList')),
  members: lazy(() => import('views/wellness/MemberManagement')),
  screenings: lazy(() => import('views/wellness/QuickScreeningForm')),
  reports: lazy(() => import('views/wellness/WellnessReports')),
};

const articleStudio = {
  dashboard: lazy(() => import('views/article-studio/Dashboard')),
  articles: lazy(() => import('views/article-studio/Articles')),
  editor: lazy(() => import('views/article-studio/ArticleEditor')),
  categories: lazy(() => import('views/article-studio/Categories')),
  audit: lazy(() => import('views/article-studio/Audit')),
};

const appRoot = DEFAULT_PATHS.APP.endsWith('/') ? DEFAULT_PATHS.APP.slice(1, DEFAULT_PATHS.APP.length) : DEFAULT_PATHS.APP;

const routesAndMenuItems = {
  mainMenuItems: [
    {
      path: DEFAULT_PATHS.APP,
      exact: true,
      redirect: true,
      to: `${appRoot}/dashboard/getting-started`,
    },
    {
      path: `${appRoot}/login`,
      component: defaultPages.login,
      label: 'Login',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/register`,
      component: defaultPages.register,
      label: 'Register',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/admin/login`,
      component: admin.login,
      label: 'Admin Login',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/blog`,
      component: blog,
      label: 'menu.blog',
      icon: 'file-text',
    },
    {
      path: `${appRoot}/upgrade`,
      label: 'menu.upgrade',
      icon: 'trend-up',
      component: upgrade,
    },
    {
      path: `${appRoot}/community`,
      label: 'menu.community',
      icon: 'messages',
      component: community.index,
      subs: [
        {
          path: '/list',
          label: 'menu.community-list',
          hideInMenu: true,
          component: community.list,
        },
      ],
    },
  ],
  sidebarItems: [
    {
      path: `${appRoot}/dashboard`,
      label: 'menu.dashboard',
      icon: 'home',
      exact: true,
      component: dashboard.gettingStarted,
      protected: true,
    },
    {
      path: `${appRoot}/user-management`,
      label: 'User Management',
      icon: 'user',
      exact: true,
      component: admin.userManagement,
      protected: true,
    },
    {
      path: `${appRoot}/admins`,
      label: 'Admins',
      icon: 'shield',
      exact: true,
      component: admin.admins,
      protected: true,
    },
    {
      path: `${appRoot}/families`,
      label: 'Families',
      icon: 'home',
      exact: true,
      component: admin.families,
      protected: true,
    },
    {
      path: `${appRoot}/providers`,
      label: 'Providers',
      icon: 'health',
      exact: true,
      component: admin.providers,
      protected: true,
    },
    {
      path: `${appRoot}/corporate-organizations`,
      label: 'Corporate Organizations',
      icon: 'building',
      exact: true,
      component: admin.corporateOrganizations,
      protected: true,
    },
    {
      path: `${appRoot}/corporate-organizations/:id`,
      label: 'Organization Details',
      component: admin.corporateOrganizationDetails,
      protected: true,
      hideInMenu: true,
    },
    {
      path: `${appRoot}/events`,
      label: 'Events',
      icon: 'calendar',
      exact: true,
      redirect: true,
      to: `${appRoot}/events/management`,
      protected: true,
      subs: [
        {
          path: '/management',
          label: 'Event Management',
          icon: 'calendar',
          component: admin.eventManagement,
        },
        {
          path: '/pending-approvals',
          label: 'Pending Approvals',
          icon: 'check-square',
          component: admin.pendingApprovals,
          badgeKey: 'pendingApprovals',
        },
        {
          path: '/bookings',
          label: 'Bookings & Tickets',
          icon: 'ticket',
          component: admin.eventBookings,
        },
        {
          path: '/:eventId/tickets',
          label: 'Tickets & Attendance',
          icon: 'file-text',
          component: admin.eventTickets,
          hideInMenu: true,
        },
      ],
    },
    {
      path: `${appRoot}/wellness-programs`,
      label: 'Wellness Programs',
      icon: 'book-open',
      exact: true,
      redirect: true,
      to: `${appRoot}/wellness-programs/templates`,
      protected: true,
      subs: [
        { 
          path: '/templates', 
          label: 'Program Templates', 
          icon: 'book-open', 
          component: admin.programTemplates, 
          protected: true
        },
      ],
    },
    {
      path: `${appRoot}/article-studio`,
      label: 'Article Studio',
      icon: 'edit',
      exact: true,
      redirect: true,
      to: `${appRoot}/article-studio/dashboard`,
      protected: true,
      subs: [
        {
          path: '/dashboard',
          label: 'Dashboard',
          icon: 'home',
          component: articleStudio.dashboard,
          protected: true,
          exact: true,
        },
        {
          path: '/articles',
          label: 'Articles',
          icon: 'file-text',
          component: articleStudio.articles,
          protected: true,
          exact: true,
        },
        {
          path: '/articles/new',
          label: 'New Article',
          component: articleStudio.editor,
          protected: true,
          hideInMenu: true,
          exact: true,
        },
        {
          path: '/articles/:id',
          label: 'Edit Article',
          component: articleStudio.editor,
          protected: true,
          hideInMenu: true,
          exact: true,
        },
        {
          path: '/categories',
          label: 'Categories',
          icon: 'tag',
          component: articleStudio.categories,
          protected: true,
          exact: true,
        },
        {
          path: '/audit',
          label: 'Audit Trail',
          icon: 'list',
          component: articleStudio.audit,
          protected: true,
          exact: true,
        },
      ],
    },
    {
      path: `${appRoot}/wellness`,
      label: 'Wellness and Tests',
      icon: 'activity',
      exact: true,
      redirect: true,
      to: `${appRoot}/wellness/dashboard`,
      protected: true,
      hideInMenu: true,
      subs: [
        {
          path: '/dashboard',
          label: 'Wellness Dashboard',
          icon: 'grid-1',
          component: wellness.dashboard,
        },
        {
          path: '/screenings/new',
          label: 'Health Screening',
          icon: 'activity',
          component: wellness.screenings,
        },
        {
          path: '/organizations',
          label: 'Corporate Programs',
          icon: 'building',
          component: wellness.organizations,
          exact: true,
        },
        {
          path: '/organizations/:organizationId/members',
          label: 'Members',
          icon: 'users',
          component: wellness.members,
          hideInMenu: true,
        },
        {
          path: '/reports',
          label: 'Reports & Analytics',
          icon: 'file-text',
          component: wellness.reports,
        },
      ],
    },
    {
      path: `${appRoot}/admin`,
      label: 'Admin',
      icon: 'shield',
      exact: true,
      redirect: true,
      to: `${appRoot}/admin/dashboard`,
      protected: true,
      hideInMenu: true,
      subs: [
        { path: '/dashboard', label: 'Dashboard', icon: 'grid-1', component: admin.dashboard, protected: true },
        { path: '/users-admin', label: 'Users Admin', icon: 'user', component: admin.users, protected: true },
        { path: '/users/:id/details', label: 'User Details', component: admin.userDetails, protected: true, hideInMenu: true, exact: true },
      ],
    },
    {
      path: `${appRoot}/dashboard`,
      label: 'menu.dashboard',
      icon: 'grid-1',
      exact: true,
      redirect: true,
      to: `${appRoot}/dashboard/getting-started`,
      hideInMenu: true,
      subs: [
        { path: '/getting-started', label: 'menu.getting-started', icon: 'navigate-diagonal', component: dashboard.gettingStarted, protected: true },
        { path: '/analysis', label: 'menu.analysis', icon: 'chart-4', component: dashboard.analysis, protected: true },
      ],
    },
    {
      path: `${appRoot}/account`,
      label: 'Account',
      icon: 'user',
      exact: true,
      redirect: true,
      to: `${appRoot}/account/settings`,
      protected: true,
      hideInMenu: true,
      subs: [
        { path: '/settings', label: 'Settings', icon: 'gear', component: account.settings, protected: true },
        { path: '/billing', label: 'Billing', icon: 'credit-card', component: account.billing, protected: true },
        { path: '/security', label: 'Security', icon: 'shield', component: account.security, protected: true },
      ],
    },
  ],
};
export default routesAndMenuItems;
